import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useBackNavigate } from '@/shared/hooks/use-back-navigate';
import {
  Button,
  Close,
  Edit,
  Select,
  Input,
  Modal,
  PageContainer,
} from '@react-apps/design-system';
import {
  CONTENT_RELATIONS,
  type ContentItem,
  type ContentItemDetail,
  type ContentLinkDetail,
  type ContentRelation,
} from '../../domain/entities/content.entity';
import { useContentItem } from '../hooks/use-content-items';
import {
  usePublishContentItem,
  useArchiveContentItem,
  useDeleteContentItem,
  useUpdateContentItemBody,
} from '../hooks/use-content-items';
import {
  useSetContentClassification,
  useLinkContent,
  useUnlinkContent,
} from '../hooks/use-content-relations';
import {
  useCanLinkContent,
  useCanWriteExams,
} from '../hooks/use-content-permissions';
import {
  CONTENT_RELATION_LABELS,
  contentTypeLabel,
  ContentStatusBadge,
  ErrorState,
  Mono,
  SectionCard,
  contentDisplayName,
  getApiErrorMessage,
  notifyDone,
  notifyFailed,
  relationHeading,
} from '../display';
import { ConfirmModal } from '../components/ConfirmModal';
import { BodyForm } from '../components/BodyForm';
import { BodyView } from '../components/BodyView';
import { QuestionCard } from '../components/QuestionCard';
import { SubjectSearch } from '../components/SubjectSearch';
import { EditLockNotice } from '../components/EditLockNotice';
import { useContentEditLock } from '../hooks/use-content-edit-lock';
import { EntityLink } from '@/shared/components/entity-link';
import { CopyButton } from '@/shared/components/copy-button';
import { IconButton } from '@/shared/components/icon-button';
import {
  Breadcrumb,
  type BreadcrumbItem,
} from '@/shared/components/breadcrumb';

export function ContentItemDetailPage() {
  const { contentId } = useParams<{ contentId: string }>();
  const { data, isLoading, isError, error } = useContentItem(contentId);

  const publish = usePublishContentItem();
  const archive = useArchiveContentItem();
  const remove = useDeleteContentItem();
  const canWriteExams = useCanWriteExams();
  // Veio do "Abrir questão" de uma prova: o caminho de volta é o breadcrumb.
  const fromExamId = (useLocation().state as { fromExamId?: string } | null)
    ?.fromExamId;
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  // Um lock para a ficha inteira: o lápis do corpo (ou os dois da questão, o
  // dela e o do comentário) entra em edição, e as relações só mudam em edição.
  const lock = useContentEditLock(data !== undefined ? contentId : undefined);

  if (isLoading) {
    return (
      <PageContainer maxWidth="5xl" spacing="none">
        {/* A linha do caminho fica reservada: quando o dado chega, o topo não
            pula. */}
        <div aria-hidden="true" className="mb-4 h-5" />
        <p role="status" className="text-(--color-text-secondary) text-sm">
          Carregando…
        </p>
      </PageContainer>
    );
  }

  if (isError || !data) {
    return (
      <PageContainer maxWidth="5xl" spacing="sm">
        <BackLink />
        <ErrorState
          message={getApiErrorMessage(error, 'Conteúdo não encontrado.')}
        />
      </PageContainer>
    );
  }

  const { item } = data;
  // A questão é editada na ficha, mas publicada e excluída na subida de prova:
  // aqui ela não tem Publicar, Excluir nem as relações (o caminho até a prova
  // é o breadcrumb, e o comentário tem a seção dele).
  const isQuestion = item.type === 'QUESTION';
  const isExam = item.type === 'EXAM';
  // Prova só se arquiva/exclui com `systemAdministrator` (`useCanWriteExams`).
  const canManage = !isExam || canWriteExams;
  const actionError = publish.error || archive.error || remove.error;
  // O título já diz prova/simulado; o rótulo embaixo sai sem o `Prova`.
  const typeLabel = contentTypeLabel(item.type, examKindOf(item));
  const owner = isQuestion ? ownerExamLink(data) : undefined;

  return (
    <PageContainer maxWidth="5xl">
      {isQuestion && <Breadcrumb items={questionBreadcrumb(owner)} />}
      {/* O "Voltar" sai só quando a pessoa veio da prova e o breadcrumb leva
          até ela. Questão tirada da prova com a ficha aberta mantém o
          `fromExamId` no `state` (sobrevive ao F5), mas não tem mais prova. */}
      {(!isQuestion || !fromExamId || !owner) && <BackLink />}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            {/* A questão tem seções de título `text-xl`: o dela sobe um passo,
                para o topo continuar acima delas. */}
            <h1
              className={`text-(--color-text-primary) font-semibold ${
                isQuestion ? 'text-2xl' : 'text-xl'
              }`}
            >
              {isQuestion ? (
                questionTitle(owner)
              ) : (
                <>
                  {typeLabel} <Mono>{item.id}</Mono>
                </>
              )}
            </h1>
            <CopyButton text={item.id} label="Copiar id" icon />
          </div>
          {data.label && (
            <p className="text-(--color-text-primary) text-sm">{data.label}</p>
          )}
          <div className="flex items-center gap-2">
            {/* Questão e prova não mostram o status: a questão se publica na
                subida de prova, e o rascunho da prova só existe durante a
                importação (DESEN-41). */}
            {!isQuestion && !isExam && (
              <ContentStatusBadge status={item.status} />
            )}
            <span className="text-(--color-text-secondary) text-xs">
              versão {item.version}
            </span>
            {item.deletedAt && (
              <span className="text-xs font-medium text-red-500">Excluído</span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {/* Só os outros tipos se publicam por aqui: a questão e a prova se
              publicam no preview, ao fim da importação (DESEN-41). */}
          {!isQuestion && !isExam && (
            <Button
              variant="secondary"
              disabled={
                item.status === 'PUBLISHED' ||
                !!item.deletedAt ||
                publish.isPending
              }
              onClick={() =>
                publish.mutate(item.id, {
                  onError: (err) => notifyFailed(err, 'Falha ao publicar'),
                })
              }
            >
              Publicar
            </Button>
          )}
          {canManage && (
            <>
              <Button
                variant="secondary"
                disabled={
                  item.status === 'ARCHIVED' ||
                  !!item.deletedAt ||
                  archive.isPending
                }
                onClick={() =>
                  archive.mutate(item.id, {
                    onError: (err) => notifyFailed(err, 'Falha ao arquivar'),
                  })
                }
              >
                Arquivar
              </Button>
              {!isQuestion && (
                <Button
                  variant="danger"
                  disabled={!!item.deletedAt}
                  onClick={() => setIsDeleteOpen(true)}
                >
                  Excluir
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {actionError && (
        <p className="text-sm text-red-600" role="alert">
          {getApiErrorMessage(actionError, 'Falha na ação')}
        </p>
      )}

      {/* A questão tem tela própria dentro da ficha: corpo, assunto e
          comentário oficial num card só, editados juntos. */}
      {isQuestion ? (
        <QuestionCard
          item={item}
          imageUrlsByFileId={data.imageUrlsByFileId ?? {}}
          lock={lock}
        />
      ) : (
        <>
          <BodySection
            item={item}
            imageUrlsByFileId={data.imageUrlsByFileId ?? {}}
            lock={lock}
          />
          {/* Prova não se classifica por assunto. */}
          {item.type !== 'EXAM' && (
            <SubjectSection
              contentId={item.id}
              data={data}
              editable={lock.editing}
            />
          )}
        </>
      )}
      {!isQuestion && (
        <LinksSection item={item} data={data} editable={lock.editing} />
      )}

      <ConfirmModal
        open={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          remove.reset();
        }}
        // Só os outros tipos: a questão se exclui na prova (card e seleção),
        // pelo caminho que ajusta a numeração.
        onConfirm={() =>
          remove.mutate(item.id, {
            onSuccess: () => setIsDeleteOpen(false),
            onError: (err) => notifyFailed(err, 'O conteúdo não foi excluído'),
          })
        }
        title="Excluir conteúdo"
        description="O item deixa de aparecer nas listagens e em tudo que o usa. O registro fica guardado, mas não dá para desfazer por aqui."
        confirmLabel="Excluir"
        loading={remove.isPending}
        error={remove.error}
      />
    </PageContainer>
  );
}

/** As buscas que guardam o estado na URL e o mandam no `state` do clique. */
const SEARCH_PATHS = ['/content/exams/search', '/content/questions/search'];

/**
 * Vindo da busca de provas (DESEN-41) ou de questões (DESEN-42), volta para
 * ela com os mesmos filtros e a mesma página, que a busca guarda na URL e
 * manda no `state` do clique.
 * Vindo de qualquer outro lugar, o "Voltar para Conteúdo" de sempre. Quem
 * chegou pela prova volta pelo breadcrumb, e este link nem aparece.
 */
function BackLink() {
  const goBack = useBackNavigate('/content');
  const navigate = useNavigate();
  const from = (useLocation().state as { from?: unknown } | null)?.from;
  const fromSearch =
    typeof from === 'string' &&
    SEARCH_PATHS.some((path) => from.startsWith(path));
  return (
    <button
      type="button"
      onClick={() => (fromSearch ? void navigate(from) : goBack())}
      className="text-(--color-text-secondary) hover:text-(--color-text-primary) text-sm"
    >
      {fromSearch ? '← Voltar para a busca' : '← Voltar para Conteúdo'}
    </button>
  );
}

/**
 * A aresta da prova dona da questão: a `CONTAINS` que chega nela. Há no máximo
 * uma (dono vs. apontador, `content.md`); os simulados chegam por
 * `REFERENCES`, e a posição deles não é o número da questão.
 */
function ownerExamLink(data: ContentItemDetail): ContentLinkDetail | undefined {
  return data.incomingLinks.find((link) => link.relation === 'CONTAINS');
}

function questionTitle(owner: ContentLinkDetail | undefined): string {
  return owner?.position != null ? `Questão ${owner.position}` : 'Questão';
}

/**
 * "Prova > Questão N", com a prova levando à tela dela. O id do link sai da
 * aresta, nunca do `location.state`, que é entrada do cliente. Sem prova dona,
 * "Conteúdo > Questão".
 */
function questionBreadcrumb(
  owner: ContentLinkDetail | undefined,
): BreadcrumbItem[] {
  if (!owner) {
    return [{ label: 'Conteúdo', to: '/content' }, { label: 'Questão' }];
  }
  const examId = owner.sourceContentId;
  // Prova que não existe mais: o id curto, ainda como link (a tela da prova
  // diz que ela não foi encontrada).
  const examName = owner.counterpart
    ? contentDisplayName({
        type: owner.counterpart.type,
        id: examId,
        label: owner.counterpart.label,
        examKind: owner.counterpart.examKind,
      })
    : `Prova ${examId.slice(0, 8)}`;
  return [
    { label: examName, to: `/content/exams/${examId}` },
    { label: questionTitle(owner) },
  ];
}

// --- Body (editável — `type` é imutável) ------------------------------------

/**
 * O lápis pede o lock do conteúdo e abre o modo edição da ficha inteira —
 * corpo, assunto e relações. Salvar o corpo não sai do modo, porque trocar o
 * assunto em seguida pediria o lock de novo; quem sai é "Concluir edição" (ou
 * Cancelar), e sair solta o lock.
 */
function BodySection({
  item,
  imageUrlsByFileId,
  lock,
}: {
  item: ContentItem;
  imageUrlsByFileId: Record<string, string>;
  lock: ReturnType<typeof useContentEditLock>;
}) {
  const update = useUpdateContentItemBody(item.id);
  // No `lost` o form continua montado, para a pessoa copiar o que escreveu.
  const editing = lock.editing || lock.lost;
  const noticeId = `edit-lock-${item.id}`;
  const canWriteExams = useCanWriteExams();
  const canEdit = item.type !== 'EXAM' || canWriteExams;

  const startEdit = () => {
    update.reset();
    void lock.start();
  };

  const finish = () => {
    update.reset();
    lock.finish();
  };

  const typeLabel = contentTypeLabel(item.type, examKindOf(item));
  return (
    <SectionCard
      title={typeLabel}
      action={
        !editing && canEdit ? (
          <IconButton
            label={`Editar ${typeLabel.toLowerCase()}`}
            icon={<Edit size="sm" />}
            onClick={startEdit}
            // Enquanto o `POST` do lock não volta, o lápis fica no lugar,
            // desabilitado: um segundo clique não manda um segundo pedido.
            disabled={!!item.deletedAt || lock.acquiring}
            blockedBy={lock.state?.status === 'locked' ? noticeId : undefined}
          />
        ) : (
          lock.editing && (
            <Button
              variant="secondary"
              size="sm"
              disabled={update.isPending}
              onClick={finish}
            >
              Concluir edição
            </Button>
          )
        )
      }
    >
      <EditLockNotice
        id={noticeId}
        state={lock.state}
        retrying={lock.acquiring}
        onRetry={startEdit}
        onExit={finish}
      />
      {editing ? (
        <BodyForm
          type={item.type}
          body={item.body}
          imageUrlsByFileId={imageUrlsByFileId}
          // Sem o lock, salvar daria 409: o botão fica desabilitado.
          pending={update.isPending || lock.lost}
          error={update.error}
          onSubmit={(result) =>
            update.mutate(
              { body: result.body, images: result.images },
              {
                onSuccess: () => notifyDone(`${typeLabel}: alterações salvas`),
                // O 409 do lock não descarta o form nem sai do modo: o texto
                // fica, com a mensagem traduzida ao lado do Salvar.
                onError: (err) => notifyFailed(err, `${typeLabel} não salvo`),
              },
            )
          }
          onCancel={finish}
        />
      ) : (
        <BodyView item={item} imageUrlsByFileId={imageUrlsByFileId} />
      )}
    </SectionCard>
  );
}

function examKindOf(item: ContentItem) {
  if (item.type !== 'EXAM') return null;
  const kind = (item.body as { examKind?: unknown }).examKind;
  return kind === 'MOCK_EXAM' || kind === 'REAL_EXAM' ? kind : null;
}

// --- Assunto (conteúdo ↔ subject) -----------------------------------------

/**
 * A ficha tem **um** assunto. O back ainda aceita `SECONDARY` e o dado legado
 * fica onde está, mas nada lê secundária: trocar o assunto grava pelo `PUT
 * subjects` (o mesmo do preview da prova), que repõe a classificação inteira
 * numa transação — uma secundária legada sai junto.
 */
function SubjectSection({
  contentId,
  data,
  editable,
}: {
  contentId: string;
  data: ContentItemDetail;
  /** Fora do modo edição, o assunto só se lê. */
  editable: boolean;
}) {
  const save = useSetContentClassification(contentId);
  const current =
    data.classifications.find((c) => c.relation === 'PRIMARY') ??
    data.classifications[0];
  // Assunto removido não mostra o id: o UUID cru é o que a ficha deixou de ter.
  const currentName = current?.subjectName ?? null;

  return (
    <SectionCard title="Assunto">
      {current ? (
        <div className="flex items-center justify-between gap-3">
          <span className="text-(--color-text-primary) text-sm">
            {currentName ?? 'Assunto removido'}
          </span>
          {editable && (
            <IconButton
              label={
                currentName
                  ? `Remover o assunto ${currentName}`
                  : 'Remover o assunto'
              }
              icon={<Close size="sm" />}
              disabled={save.isPending}
              onClick={() => save.mutate({ subjectIds: [] })}
            />
          )}
        </div>
      ) : (
        <p className="text-(--color-text-secondary) text-sm">Sem assunto.</p>
      )}

      {editable && (
        <SubjectSearch
          label={current ? 'Trocar assunto' : 'Definir assunto'}
          excludeId={current?.subjectId}
          disabled={save.isPending}
          onPick={(subject) => save.mutate({ subjectIds: [subject.id] })}
        />
      )}

      {save.error != null && (
        <p className="text-sm text-red-600" role="alert">
          {getApiErrorMessage(save.error, 'Falha ao gravar o assunto')}
        </p>
      )}
    </SectionCard>
  );
}

// --- Relações (conteúdo ↔ conteúdo) ----------------------------------------

type LinkRow = { link: ContentLinkDetail; side: 'source' | 'target' };

function LinksSection({
  item,
  data,
  editable,
}: {
  item: ContentItem;
  data: ContentItemDetail;
  /** Fora do modo edição, as relações só se leem. */
  editable: boolean;
}) {
  const canLinkAny = useCanLinkContent();
  const canWriteExams = useCanWriteExams();
  const unlink = useUnlinkContent(item.id);

  // Um bloco por (relação, lado), na ordem do vocabulário: o título já diz a
  // direção, então "saindo/chegando" deixa de ser seção.
  const groups = CONTENT_RELATIONS.flatMap((relation) =>
    (['source', 'target'] as const).map((side) => ({
      relation,
      side,
      rows: (side === 'source' ? data.outgoingLinks : data.incomingLinks)
        .filter((l) => l.relation === relation)
        .map((link): LinkRow => ({ link, side })),
    })),
  ).filter((g) => g.rows.length > 0);

  const [linking, setLinking] = useState(false);
  const isExam = item.type === 'EXAM';
  // Relacionar a partir de prova é do `systemAdministrator`; desvincular (o
  // "x") segue aberto, como no back.
  // Vincular e desvincular só em edição (com o lock), e prova só com
  // `systemAdministrator` (DESEN-41).
  const canLink = canLinkAny && editable && (!isExam || canWriteExams);
  const hasQuestions = data.outgoingLinks.some(
    (l) => l.relation === 'CONTAINS',
  );

  return (
    <SectionCard
      title="Relações"
      action={
        canLink && (
          <Button variant="secondary" onClick={() => setLinking(true)}>
            Adicionar nova relação
          </Button>
        )
      }
    >
      {canLink && (
        <LinkModal
          contentId={item.id}
          open={linking}
          onClose={() => setLinking(false)}
        />
      )}

      {isExam && !hasQuestions && (
        <p className="text-(--color-text-secondary) text-sm">
          Nenhuma questão nesta prova.
        </p>
      )}
      {!isExam && groups.length === 0 && (
        <p className="text-(--color-text-secondary) text-sm">
          Nenhuma relação.
        </p>
      )}

      {unlink.error != null && (
        <p className="text-sm text-red-600" role="alert">
          {getApiErrorMessage(unlink.error, 'Falha ao desvincular')}
        </p>
      )}

      <div className="space-y-4">
        {groups.map((group) => (
          <div key={`${group.relation}-${group.side}`}>
            <h3 className="text-(--color-text-secondary) mb-2 text-xs font-medium uppercase tracking-wide">
              {relationHeading(group.relation, group.side, item.type)} (
              {group.rows.length})
            </h3>
            <ul className="divide-(--color-border) divide-y">
              {group.rows.map(({ link, side }) => {
                const otherId =
                  side === 'source'
                    ? link.targetContentId
                    : link.sourceContentId;
                const name = link.counterpart
                  ? contentDisplayName({
                      type: link.counterpart.type,
                      id: otherId,
                      label: link.counterpart.label,
                      examKind: link.counterpart.examKind,
                      position: side === 'source' ? link.position : null,
                    })
                  : otherId;
                return (
                  <li
                    key={link.id}
                    className="flex items-start justify-between gap-3 py-2"
                  >
                    <div className="min-w-0 flex-1 text-sm">
                      <EntityLink kind="contentItem" id={otherId}>
                        {name}
                      </EntityLink>
                      {/* A questão se reconhece pelo começo do enunciado, não
                          pelo id (DESEN-41): pelo menos 3 linhas. */}
                      {link.counterpart?.excerpt && (
                        <p className="text-(--color-text-secondary) mt-1 line-clamp-3 text-sm">
                          {link.counterpart.excerpt}
                        </p>
                      )}
                    </div>
                    {side === 'source' && canLink && (
                      <IconButton
                        label={
                          isExam && link.relation === 'CONTAINS'
                            ? `Tirar ${name} da prova`
                            : `Desvincular ${name}`
                        }
                        icon={<Close size="sm" />}
                        // Sem confirmação: o clique duplo dispararia duas vezes.
                        disabled={unlink.isPending}
                        onClick={() =>
                          unlink.mutate({
                            targetContentId: link.targetContentId,
                            relation: link.relation,
                          })
                        }
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

/** Relacionar por id — só para quem a rota aceita (`useCanLinkContent`). */
function LinkModal({
  contentId,
  open,
  onClose,
}: {
  contentId: string;
  open: boolean;
  onClose: () => void;
}) {
  const [targetId, setTargetId] = useState('');
  const [relation, setRelation] = useState<ContentRelation>(
    CONTENT_RELATIONS[0],
  );
  const [position, setPosition] = useState('');
  const link = useLinkContent(contentId);

  const close = () => {
    link.reset();
    setTargetId('');
    setPosition('');
    onClose();
  };

  const canAdd =
    targetId.trim() !== '' && targetId.trim() !== contentId && !link.isPending;

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canAdd) return;
    link.mutate(
      {
        targetContentId: targetId.trim(),
        relation,
        ...(position ? { position: Number(position) } : {}),
      },
      { onSuccess: close },
    );
  };

  return (
    <Modal open={open} onClose={close} title="Adicionar nova relação">
      <form onSubmit={handleAdd} className="space-y-4">
        <Input
          label="Id do conteúdo"
          value={targetId}
          onChange={(e) => setTargetId(e.target.value)}
          placeholder="id do conteúdo a relacionar…"
        />
        <Select
          label="Relação"
          value={relation}
          onChange={(e) => setRelation(e.target.value as ContentRelation)}
          options={CONTENT_RELATIONS.map((r) => ({
            value: r,
            label: CONTENT_RELATION_LABELS[r],
          }))}
        />
        <Input
          label="Posição (opcional)"
          type="number"
          value={position}
          onChange={(e) => setPosition(e.target.value)}
        />
        {link.error && (
          <p className="text-sm text-red-600">
            {getApiErrorMessage(link.error, 'Falha ao relacionar')}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={close}>
            Cancelar
          </Button>
          <Button type="submit" disabled={!canAdd}>
            {link.isPending ? 'Relacionando…' : 'Relacionar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
